import React, { createContext, useContext, useState, useRef, useEffect, useCallback, useMemo } from "react";
import { getFirestore, doc, getDoc, onSnapshot } from 'firebase/firestore';
import { getStorage, ref, getDownloadURL } from 'firebase/storage';
import { getFunctions, httpsCallable } from "firebase/functions";
import { useNavigate, useLocation } from "react-router-dom"; 
import { getDecryptedTrackBlob } from "../services/offlineStorage";
import toast from 'react-hot-toast';

// A standard algorithm to shuffle an array randomly
const shuffleArray = (array) => {
    let currentIndex = array.length, randomIndex;
    while (currentIndex !== 0) {
        randomIndex = Math.floor(Math.random() * currentIndex);
        currentIndex--;
        [array[currentIndex], array[randomIndex]] = [array[randomIndex], array[currentIndex]];
    }
    return array;
};

// 1. Create the single context
const MusicContext = createContext();

// 2. Create the single custom hook for consumers
export const useMusic = () => useContext(MusicContext);

// 3. Define the single, unified provider
export const MusicProvider = ({ children }) => {
    // --- STATE FROM MusicContext ---
    const [currentSong, setCurrentSong] = useState(null);
    const [previousSong, setPreviousSong] = useState(null);
    const [nextSong, setNextSong] = useState(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [returnToPath, setReturnToPath] = useState('/');
    const [playSource, setPlaySource] = useState("");
    const [masterQueue, setMasterQueue] = useState([]);
    const [shuffledQueue, setShuffledQueue] = useState([]);
    const [playHistory, setPlayHistory] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isShuffleOn, setIsShuffleOn] = useState(false);
    const [repeatMode, setRepeatMode] = useState('off');
    const [hydratedSongs, setHydratedSongs] = useState({});
    const [isMuted, setIsMuted] = useState(false);
    const [isMicroPlayer, setIsMicroPlayer] = useState(false);

    // --- STATE FROM TrendsQueueContext ---
    const [trendsQueue, setTrendsQueue] = useState([]);
    const [trendsCurrentIndex, setTrendsCurrentIndex] = useState(0);
    const [trendsIsLoading, setTrendsIsLoading] = useState(true);
    const [totalSessionRatings, setTotalSessionRatings] = useState(0);
    const isRefreshingQueue = useRef(false);
    const lastRefreshCount = useRef(0);

    // const [trendsDisplayedSong, setTrendsDisplayedSong] = useState(null);
    // const [trendsIsReadyToPlay, setTrendsIsReadyToPlay] = useState(false);

    // --- REFS & FIREBASE ---
    const audioRef = useRef(new Audio());
    const radioUnsubscribeRef = useRef(null);
    const radioDataRef = useRef(null);
    const playbackEvents = useRef([]);
    const lastLoggedId = useRef(null);
    const lastKnownTimeRef = useRef(0);
    const isInitializedRef = useRef(false);
    const hasDoneFirstRefresh = useRef(false);
    const db = getFirestore();
    const functions = getFunctions();
    const navigate = useNavigate();
    const location = useLocation(); 

    // By wrapping this in useMemo with an empty dependency array [],
    // we ensure the logStream function is created only once and its reference remains stable.
    const logStream = useMemo(() => httpsCallable(functions, 'logStream'), [functions]);
    
    /**
     * ⚡ OFFLINE AUDIO RESOLVER
     * Resolves playable audio URL: checks local encrypted IndexedDB blob first when offline.
     */
    const getPlayableAudioUrl = useCallback(async (song) => {
        if (!song?.id) return null;

        // 1. Check local IndexedDB encrypted cache first
        const localBlobUrl = await getDecryptedTrackBlob(song.id);
        if (localBlobUrl) {
            console.log(`⚡ [MusicContext] Loaded encrypted offline blob for song: ${song.id}`);
            return localBlobUrl;
        }

        // 2. If device is offline and no local blob exists, warn user and stop
        if (!navigator.onLine) {
            toast.error("Track not available offline. Tip 500 pts to unlock offline caching!", {
                id: `offline-missing-${song.id}`,
            });
            return null;
        }

        // 3. Online fallback: use resolved remote audio URL
        return song.resolvedAudioUrl || song.audioURL;
    }, []);

    // --- RADIO SYNC LOGIC ---
    const getRadioSyncData = useCallback((radioData) => {
        if (!radioData || !radioData.tracks) return null;
        const { tracks, scheduleStartTime } = radioData;
        const totalElapsed = (Date.now() - scheduleStartTime.toMillis()) / 1000;
        let runningTime = 0;

        for (const track of tracks) {
            if (totalElapsed < runningTime + track.duration) {
                return { track, seekPosition: totalElapsed - runningTime };
            }
            runningTime += track.duration;
        }
        return null;
    }, []);

    // Helper to calculate and apply the sync
    const performRadioSync = useCallback((data) => {
        if (playSource !== 'Radio') return;
        if (!data || !data.tracks || !data.scheduleStartTime) return;

        const { tracks, scheduleStartTime } = data;
        const startTime = scheduleStartTime.toMillis();
        const now = Date.now();
        
        let totalElapsed = (now - startTime) / 1000;
        let runningTime = 0;
        let targetTrack = null;
        let seekPosition = 0;

        for (const track of tracks) {
            const trackDuration = Number(track.duration) || 210;
            if (totalElapsed < runningTime + trackDuration) {
                targetTrack = track;
                seekPosition = totalElapsed - runningTime;
                break;
            }
            runningTime += trackDuration;
        }

        if (targetTrack) {
            const trackId = targetTrack.id || targetTrack.fileName;

            if (currentSong?.id !== trackId) {

                
                // ✅ FORCE DUMP PREVIOUS TRACK EVENTS BEFORE OVERWRITING
                if (currentSong && playbackEvents.current.length > 0 && lastLoggedId.current !== currentSong.id) {
                    const closingTime = lastKnownTimeRef.current || audioRef.current.currentTime;
                    const finalEvents = [
                        ...playbackEvents.current,
                        { type: 'pause', time: closingTime, timestamp: Date.now() }
                    ];
                    
                    if (currentSong.artistId && currentSong.type !== 'program_segment') {

                        logStream({
                            songId: currentSong.id,
                            artistId: currentSong.artistId,
                            source: playSource, // Will capture original source (e.g. Trends)
                            events: finalEvents,
                        }).catch(err => console.error("RadioSync flush failed:", err));
                    }
                    lastLoggedId.current = currentSong.id;
                }

                // Now safe to prime the logs for the incoming Live Radio track
                playbackEvents.current = [
                    { type: 'play', time: seekPosition, timestamp: Date.now() }
                ];

                setCurrentSong({ ...targetTrack, id: trackId });
                audioRef.current.src = targetTrack.audioURL || targetTrack.resolvedAudioUrl;
                audioRef.current.currentTime = seekPosition;
                audioRef.current.play().catch(e => console.log("Playback blocked", e));
                setIsPlaying(true);
            } else {
                // Drift check remains the same
                const drift = Math.abs(audioRef.current.currentTime - seekPosition);
                if (drift > 3) audioRef.current.currentTime = seekPosition;
            }
        }
    }, [currentSong, logStream, playSource]);

    const tuneInToRadio = useCallback(() => {
        if (radioUnsubscribeRef.current) radioUnsubscribeRef.current();
        
        setPlaySource('Radio');

        const unsubscribe = onSnapshot(doc(db, "radio", "main"), (docSnap) => {
            if (docSnap.exists()) {
                const data = docSnap.data();
                radioDataRef.current = data;
                performRadioSync(data);
            }
        });

        radioUnsubscribeRef.current = unsubscribe;
        return unsubscribe;
    }, [db, performRadioSync]);

    const leaveRadio = useCallback(() => {
        if (radioUnsubscribeRef.current) {
            radioUnsubscribeRef.current();
            radioUnsubscribeRef.current = null;
        }
        radioDataRef.current = null;
    }, []);

    const toggleMute = () => {
        setIsMuted(prev => {
            const newMutedState = !prev;
            // Apply the actual volume change to the audio element
            if (audioRef.current) {
            audioRef.current.muted = newMutedState;
            }
            return newMutedState;
        });
    };

    const setMuteExplicit = (val) => {
        setIsMuted(val);
        if (audioRef.current) {
            audioRef.current.muted = val;
        }
    };

    // --- HYDRATION & CORE PLAYBACK FUNCTIONS (from MusicContext) ---

    // This comprehensive hydration function will be used by all parts of the context

    const getHydratedSong = useCallback(async (songInput, providedData = null) => {
        if (!songInput) return null;

        // 1. SAFELY EXTRACT STRING ID (Handles both raw string IDs and song objects)
        const songId = typeof songInput === "object" ? (songInput.id || songInput.songId) : songInput;

        if (!songId || typeof songId !== "string") {
            console.error("❌ getHydratedSong received an invalid songId:", songInput);
            return null;
        }

        // 2. Check if this is a Special Event segment (Pre-hydrated)
        if (providedData && (providedData.type === 'program_segment' || providedData.isHydrated)) {
            return {
                ...providedData,
                resolvedAudioUrl: providedData.audioURL || providedData.resolvedAudioUrl,
                resolvedArtworkUrl: providedData.albumArtworkURL || providedData.resolvedArtworkUrl
            };
        }
        
        // 3. Read from state cache first (indexed by string songId)
        if (hydratedSongs[songId]) {
            return hydratedSongs[songId];
        }

        // 4. Offline guard for hydration
        if (!navigator.onLine) {
            const localBlobUrl = await getDecryptedTrackBlob(songId);
            if (localBlobUrl) {
                const fallbackSong = {
                    id: songId,
                    title: providedData?.title || "Offline Track",
                    artistName: providedData?.artistName || "Artist",
                    resolvedAudioUrl: localBlobUrl,
                    resolvedArtworkUrl: providedData?.albumArtworkURL || null,
                };
                setHydratedSongs(prev => ({ ...prev, [songId]: fallbackSong }));
                return fallbackSong;
            }
            return null;
        }

        try {
            const storage = getStorage();
            const songDoc = await getDoc(doc(db, "songs", songId));
            if (!songDoc.exists()) {
                return null;
            }
            const songData = { id: songDoc.id, ...songDoc.data() };

            const [artworkUrl, audioUrl, artistData] = await Promise.all([
                getDownloadURL(ref(storage, songData.albumArtworkURL)).catch(() => null),
                getDownloadURL(ref(storage, songData.audioURL)).catch(() => null),
                songData.artistId ? getDoc(doc(db, "artist_accounts", songData.artistId)).then(d => d.exists() ? d.data() : {}) : Promise.resolve({})
            ]);

            if (!audioUrl) {
                return null;
            }

            const fullyHydratedSong = {
                ...songData,
                resolvedArtworkUrl: artworkUrl,
                resolvedAudioUrl: audioUrl,
                artistName: artistData.artistName || artistData.displayName || "Unknown Artist",
                artistProfileURL: artistData.photoURL || null,
                artistHandle: artistData.handle || null,
            };

            // Write fully hydrated object to state cache indexed by string ID
            setHydratedSongs(prev => ({ ...prev, [songId]: fullyHydratedSong }));

            return fullyHydratedSong;
        } catch (error) {
            console.error(`Error hydrating song ${songId}:`, error);
            return null;
        }
    }, [db, hydratedSongs]);


    const playSong = useCallback(async (songObject) => {
        if (!songObject?.id) return;

        // Resolve playable source (local blob or remote URL)
        const playableUrl = await getPlayableAudioUrl(songObject);

        if (!playableUrl) {
            setIsPlaying(false);
            return;
        }

        if (audioRef.current) {
            audioRef.current.muted = isMuted;
            if (audioRef.current.src !== playableUrl) {
                audioRef.current.src = playableUrl;
            }
        }

        try {
            await audioRef.current.play();
            setIsPlaying(true);
            setCurrentSong({ ...songObject, playableUrl });
        } catch (error) {
            if (error.name === 'AbortError') {
                
            } else {
                console.error("Playback Error:", error);
            }
        }
    }, [isMuted, getPlayableAudioUrl]);

    const loadSong = useCallback(async (songObject) => {
        if (!songObject?.id) return;

        const playableUrl = await getPlayableAudioUrl(songObject);
        if (!playableUrl) return;

        setCurrentSong({ ...songObject, playableUrl });
        setIsPlaying(false);

        const audio = audioRef.current;
        if (audio) {
            audio.muted = isMuted;
            if (audio.src !== playableUrl) {
                audio.removeAttribute('src');
                audio.src = playableUrl;
                audio.load();
            }
            audio.pause();
        }
    }, [isMuted, getPlayableAudioUrl]);

    // Helper to calculate dynamic labels like "Top Twenty: #3 (11th May 2026)"
    const getDynamicSource = useCallback((baseSource, queue, index) => {
        // 🌟 UPDATED: Matches both "Top Twenty" (for legacy charts) and your new "Top Fifty" source tags
        if (baseSource && (baseSource.startsWith('Top Twenty') || baseSource.startsWith('Top Fifty'))) {
            const position = queue[index]?.position || (index + 1);
            const chartDate = sessionStorage.getItem('chart_date') || "Latest";
            
            // Extract base label cleanly (Top Twenty or Top Fifty)
            const label = baseSource.startsWith('Top Fifty') ? 'Top Fifty' : 'Top Twenty';
            return `${label}: #${position} (${chartDate})`;
        }
        return baseSource;
    }, []);

    // File: src/context/MusicContext.js

    const setQueueAndPlay = useCallback(async (queueRefs, startIndex, source = {}) => {
        // 🌟 Kill any active radio listeners immediately
        if (source !== 'Radio') {
            leaveRadio();
        }

        isInitializedRef.current = true;
        const firstSongId = queueRefs[startIndex]?.id;
        if (!firstSongId) return;

        const hydratedFirstSong = await getHydratedSong(firstSongId, queueRefs[startIndex]);
        if (hydratedFirstSong) {
            if (audioRef.current) {
                audioRef.current.muted = false;
            }
            setIsMuted(false);

            const formattedSource = getDynamicSource(source, queueRefs, startIndex);
            
            setMasterQueue(queueRefs);
            setShuffledQueue(shuffleArray([...queueRefs]));
            setPlayHistory([firstSongId]);
            setPlaySource(formattedSource);
            setCurrentIndex(startIndex);
            setCurrentSong(hydratedFirstSong);

            await playSong(hydratedFirstSong);
        }
    }, [
        leaveRadio,
        getHydratedSong, 
        playSong, 
        setMasterQueue, 
        setShuffledQueue, 
        setPlayHistory, 
        setPlaySource, 
        setCurrentIndex, 
        setCurrentSong,
        setIsMuted,
        getDynamicSource
    ]);

    // --- TRENDS-SPECIFIC FUNCTIONS (formerly in TrendsQueueContext) ---

    const [hasDismissedTrendsIntro, setHasDismissedTrendsIntro] = useState(false);

    const dismissTrendsIntro = () => {
        setHasDismissedTrendsIntro(true);
    };

    const startTrendsPlayback = useCallback(() => {
        if (trendsQueue.length > 0) {
            setQueueAndPlay(trendsQueue, trendsCurrentIndex, 'Trends');
        }
    }, [trendsQueue, trendsCurrentIndex, setQueueAndPlay]);

    const goToTrend = useCallback(async (newIndex) => {
        if (newIndex < 0 || newIndex >= trendsQueue.length) return;

        // 🌟 Ensure radio is stopped when navigating trends
        leaveRadio();
        setPlaySource('Trends');

        setTrendsCurrentIndex(newIndex);
        
        const songToPlay = await getHydratedSong(trendsQueue[newIndex]?.id);
        if (songToPlay) {
            if (masterQueue !== trendsQueue) {
                setMasterQueue(trendsQueue);
            }
            await playSong(songToPlay);
        }
    }, [trendsQueue, getHydratedSong, playSong, masterQueue, leaveRadio]);

    //Is this function able to be removed with the sunsetting of Daily Streaks?
    const incrementRatingCount = useCallback(() => {
        setTotalSessionRatings(prev => prev + 1);
    }, []);

    const replaceUpcomingQueue = useCallback((newSongRefs = [], anchorSongId, bufferSize = 0, append = false) => {
        // If append flag is true, just add the new songs to the end of the existing queue
        if (append) {
            setMasterQueue(prevQueue => [...prevQueue, ...newSongRefs]);
            setShuffledQueue(prevQueue => [...prevQueue, ...newSongRefs]); // Or re-shuffle if needed
            return;
        }
        // If no anchor is provided, just replace the whole queue.
            if (!anchorSongId) {
            setMasterQueue(newSongRefs);
            setShuffledQueue(shuffleArray(newSongRefs));
            return;
        }

        setMasterQueue(prevQueue => {
            const preserveFromIndex = prevQueue.findIndex(song => song.id === anchorSongId);
            // If the anchor isn't found, fall back to replacing the whole queue.
            if (preserveFromIndex === -1) return newSongRefs;

            const preserveUntilIndex = preserveFromIndex + 1 + bufferSize;
            const preservedQueue = prevQueue.slice(0, preserveUntilIndex);

            const preservedIds = new Set(preservedQueue.map(s => s.id));
            const uniqueNewRefs = newSongRefs.filter(s => !preservedIds.has(s.id));
            const finalQueue = [...preservedQueue, ...uniqueNewRefs];
            
            setShuffledQueue(shuffleArray(finalQueue));
            return finalQueue;
        });
    }, []); // This function no longer depends on currentSong.

    // File: src/context/MusicContext.js

    const playNext = useCallback(async () => {
        // 1. Radio Guard
        if (playSource === 'Radio') {
            toast.error("You can't skip tracks on live radio!");
            return;
        }

        // 2. Trends / Swipe Feed case
        if (playSource === 'Trends') {
            await goToTrend(trendsCurrentIndex + 1);
            return;
        }

        // 3. Queue Playback
        const activeQueue = isShuffleOn ? shuffledQueue : masterQueue;
        if (!activeQueue || activeQueue.length === 0) return;

        let nextIndex;
        let nextSongRef = null;

        if (isShuffleOn) {
            // Find unplayed songs from history
            const unplayed = activeQueue.filter((s) => !playHistory.includes(s.id));

            if (unplayed.length > 0) {
                nextSongRef = unplayed[Math.floor(Math.random() * unplayed.length)];
            } else {
                // Reset history when all tracks have been played
                setPlayHistory([]);
                nextSongRef = activeQueue[Math.floor(Math.random() * activeQueue.length)];
            }

            if (nextSongRef) {
                const foundIndex = masterQueue.findIndex((s) => s.id === nextSongRef.id);
                nextIndex = foundIndex !== -1 ? foundIndex : 0;
                // Record track in history
                setPlayHistory((prev) => [...prev, nextSongRef.id]);
            }
        } else {
            // Sequential playback
            nextIndex = (currentIndex + 1) % activeQueue.length;
            nextSongRef = activeQueue[nextIndex];
        }

        if (!nextSongRef) return;

        // 🌟 4. SYNCHRONOUS PLAYBACK (Keeps Lock-Screen Audio Session Alive)
        const targetAudioUrl = nextSongRef.audioURL || nextSongRef.songURL || nextSongRef.url;

        if (audioRef.current && targetAudioUrl) {
            audioRef.current.src = targetAudioUrl;
            // Always initiate playback when advancing queue tracks
            audioRef.current.play().then(() => {
                setIsPlaying(true);
            }).catch((err) => {
                console.warn("Background auto-play caught error:", err);
            });
        }

        // Update indices and current song immediately in memory
        setCurrentIndex(nextIndex);
        setCurrentSong(nextSongRef);

        // 🌟 5. ROUTE-AWARE SYNC (Only navigate if the user is already on /song/)
        if (typeof window !== 'undefined' && window.location.pathname.startsWith('/song/')) {
            navigate(`/song/${nextSongRef.id}`, { replace: true, state: { fromPlayer: true } });
        }

        // 🌟 6. ASYNCHRONOUS HYDRATION (Runs in background without stalling audio)
        try {
            if (typeof getHydratedSong === 'function') {
                const hydratedNextSong = await getHydratedSong(nextSongRef.id);
                if (hydratedNextSong) {
                    const updatedSource = getDynamicSource ? getDynamicSource(playSource, activeQueue, nextIndex) : playSource;
                    setPlaySource(updatedSource);
                    setCurrentSong((prev) => ({ ...prev, ...hydratedNextSong }));

                    // Keep playback state aligned with hydrated song
                    if (isPlaying) {
                        await playSong(hydratedNextSong);
                    } else {
                        loadSong(hydratedNextSong);
                    }
                }
            }
        } catch (err) {
            console.error("Background song hydration failed:", err);
        }
    }, [
        playSource,
        goToTrend,
        trendsCurrentIndex,
        isShuffleOn,
        masterQueue,
        shuffledQueue,
        playHistory,
        setPlayHistory,
        currentIndex,
        setCurrentIndex,
        getHydratedSong,
        getDynamicSource,
        setPlaySource,
        isPlaying,
        playSong,
        loadSong,
        navigate,
    ]);


    const playPrevious = useCallback(async () => {
        // 1. Radio Guard
        if (playSource === 'Radio') {
            toast.error("Radio is a live stream!");
            return;
        }

        // 2. Trends / Swipe Feed case
        if (playSource === 'Trends') {
            await goToTrend(trendsCurrentIndex - 1);
            return;
        }

        const activeQueue = isShuffleOn ? shuffledQueue : masterQueue;
        if (!activeQueue || activeQueue.length === 0) return;

        // 3. Shuffle Mode Previous Logic
        if (isShuffleOn) {
            const unplayed = activeQueue.filter(s => !playHistory.includes(s.id));
            let targetSongRef = null;

            if (unplayed.length > 0) {
                targetSongRef = unplayed[Math.floor(Math.random() * unplayed.length)];
            } else {
                setPlayHistory([]);
                targetSongRef = activeQueue[Math.floor(Math.random() * activeQueue.length)];
            }

            if (targetSongRef) {
                const newIndex = masterQueue.findIndex(s => s.id === targetSongRef.id);
                const hydratedNextSong = await getHydratedSong(targetSongRef.id);
                if (hydratedNextSong) {
                    setCurrentIndex(newIndex !== -1 ? newIndex : 0);
                    if (isPlaying) {
                        await playSong(hydratedNextSong);
                    } else {
                        loadSong(hydratedNextSong);
                    }

                    if (typeof window !== 'undefined' && window.location.pathname.startsWith('/song/')) {
                        navigate(`/song/${targetSongRef.id}`, { replace: true, state: { fromPlayer: true } });
                    }
                }
            }
            return;
        }

        // 4. Sequential Previous Logic
        const prevIndex = (currentIndex - 1 + activeQueue.length) % activeQueue.length;
        const prevSongRef = activeQueue[prevIndex];

        if (prevSongRef) {
            const hydratedPrevSong = await getHydratedSong(prevSongRef.id);
            if (hydratedPrevSong) {
                const updatedSource = getDynamicSource ? getDynamicSource(playSource, activeQueue, prevIndex) : playSource;
                setPlaySource(updatedSource);
                setCurrentIndex(prevIndex);

                // 🛑 Fixed double-play invocation:
                if (isPlaying) {
                    await playSong(hydratedPrevSong);
                } else {
                    loadSong(hydratedPrevSong);
                }

                // 🌟 Route-aware sync: Only update URL if user is actively on the full song page
                if (typeof window !== 'undefined' && window.location.pathname.startsWith('/song/')) {
                    navigate(`/song/${prevSongRef.id}`, { replace: true, state: { fromPlayer: true } });
                }
            }
        }
    }, [
        playSource,
        goToTrend,
        trendsCurrentIndex,
        isShuffleOn,
        masterQueue,
        shuffledQueue,
        playHistory,
        setPlayHistory,
        currentIndex,
        setCurrentIndex,
        getHydratedSong,
        getDynamicSource,
        setPlaySource,
        isPlaying,
        playSong,
        loadSong,
        navigate,
    ]);

    const pauseSong = useCallback(() => {
        audioRef.current.pause();
        setIsPlaying(false);
        // Log the pause event
        playbackEvents.current.push({ type: 'pause', time: audioRef.current.currentTime, timestamp: Date.now() });
    }, []);


    const togglePlayPause = useCallback(() => {
        if (!currentSong) return;
        if (isPlaying) {
            pauseSong();
        } else {
            // Log the resume event
            playbackEvents.current.push({ type: 'play', time: audioRef.current.currentTime, timestamp: Date.now() });
            audioRef.current.play();
            setIsPlaying(true);
        }
    }, [isPlaying, currentSong, pauseSong]);


    const toggleShuffle = useCallback(() => {
        setIsShuffleOn(prev => !prev);
    }, []);

    const toggleRepeatMode = useCallback(() => {
        setRepeatMode(currentMode => {
            if (currentMode === 'off') return 'playlist';
            if (currentMode === 'playlist') return 'song';
            if (currentMode === 'song') return 'off';
            return 'off';
        });
    }, []);

    const setQueueAndLoad = useCallback(async (queueRefs = [], startIndex = 0, source = "") => {
        if (source !== 'Radio') {
            leaveRadio();
        }
        isInitializedRef.current = true;
        if (!queueRefs.length) return;
        const songToLoadId = queueRefs[startIndex]?.id;
        if (!songToLoadId) return;

        const hydratedSong = await getHydratedSong(songToLoadId);
        if (hydratedSong) {
            setMasterQueue(queueRefs);
            setShuffledQueue(shuffleArray([...queueRefs]));
            setPlayHistory([songToLoadId]);
            setPlaySource(source);
            setCurrentIndex(startIndex);
            
            setCurrentSong(hydratedSong);
            setIsPlaying(false);

            if (audioRef.current.src !== hydratedSong.resolvedAudioUrl) {
                audioRef.current.src = hydratedSong.resolvedAudioUrl;
                audioRef.current.load();
            }
        }
    }, [getHydratedSong, leaveRadio]);

    // 🌟 Helper to standardize song object mapping across initial and refresh fetches
    const mapSongQueueResponse = (rawQueue = []) => {
        return rawQueue.map(item => ({
            id: typeof item === "object" ? (item.songId || item.id) : item,
            isBoosted: typeof item === "object" ? Boolean(item.isBoosted) : false
        })).filter(item => Boolean(item.id));
    };

    // --- ALL EFFECTS (COMBINED) ---

    // --- INITIALIZE TRENDS QUEUE ---
    useEffect(() => {
        const initializeTrendsQueue = async () => {
            setTrendsIsLoading(true);
            try {
                const generateRecommendations = httpsCallable(functions, 'generateRecommendations');
                const result = await generateRecommendations();
                const songQueueFromServer = result.data?.songQueue || [];

                if (songQueueFromServer.length > 0) {
                    // 1. Normalize data structure
                    const queueWithBoostFlag = mapSongQueueResponse(songQueueFromServer);
                    setTrendsQueue(queueWithBoostFlag);

                    // Save to sessionStorage for page reload persistence
                    sessionStorage.setItem('trendsQueue', JSON.stringify(queueWithBoostFlag));

                    // 2. Staggered Loading: Await initial 5 songs for immediate rendering
                    const songsToPreloadImmediately = queueWithBoostFlag.slice(0, 5);
                    await Promise.all(songsToPreloadImmediately.map(song => getHydratedSong(song.id)));

                    // 3. Preload the rest in background
                    const remainingSongs = queueWithBoostFlag.slice(5);
                    if (remainingSongs.length > 0) {
                        Promise.all(remainingSongs.map(song => getHydratedSong(song.id)));
                    }
                }
            } catch (error) {
                console.error("Failed to initialize Trends queue:", error);
            } finally {
                setTrendsIsLoading(false);
            }
        };

        initializeTrendsQueue();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [functions]);


    // --- REFRESH TRENDS QUEUE ---
    const refreshTrendsQueue = useCallback(async (currentIndex, currentQueue) => {
        if (isRefreshingQueue.current) return;
        isRefreshingQueue.current = true;
        
        try {
            const generateRecommendations = httpsCallable(functions, 'generateRecommendations');
            const result = await generateRecommendations();
            const rawQueue = result.data?.result?.songQueue || result.data?.songQueue || [];
            
            // 1. Normalize data structure (Preserves isBoosted flag)
            const newSongRefs = mapSongQueueResponse(rawQueue);

            // 2. Filter out existing songs in current queue
            const existingIds = new Set(currentQueue.map(s => s.id));
            const uniqueNewRefs = newSongRefs.filter(s => !existingIds.has(s.id));

            if (uniqueNewRefs.length === 0) {
                isRefreshingQueue.current = false;
                return;
            }

            // 3. Preload upcoming batch
            const songsToPreload = uniqueNewRefs.slice(0, 5);
            Promise.all(songsToPreload.map(song => getHydratedSong(song.id)));

            const bufferSize = 4;
            const anchorSongId = currentQueue[currentIndex]?.id;

            replaceUpcomingQueue(uniqueNewRefs, anchorSongId, bufferSize);
            
            // 4. Update state preserving played songs + upcoming buffer
            setTrendsQueue(prevTrendsQueue => {
                const currentId = prevTrendsQueue[currentIndex]?.id;
                if (!currentId) return [...prevTrendsQueue, ...uniqueNewRefs];

                const preserveFromIndex = prevTrendsQueue.findIndex(song => song.id === currentId);
                if (preserveFromIndex === -1) return [...prevTrendsQueue, ...uniqueNewRefs];

                const preserveUntilIndex = preserveFromIndex + 1 + bufferSize;
                const preservedQueue = prevTrendsQueue.slice(0, preserveUntilIndex);
                const preservedIds = new Set(preservedQueue.map(s => s.id));
                
                const filteredNewRefs = uniqueNewRefs.filter(s => !preservedIds.has(s.id));
                const finalTrendsQueue = [...preservedQueue, ...filteredNewRefs];

                // Save structured queue objects to sessionStorage
                sessionStorage.setItem('trendsQueue', JSON.stringify(finalTrendsQueue));
                return finalTrendsQueue;
            });

        } catch (error) {
            console.error("Error refreshing trends queue:", error);
        } finally {
            isRefreshingQueue.current = false;
        }
    }, [functions, replaceUpcomingQueue, getHydratedSong]);

    const closePlayer = () => {
        // This function now knows exactly where to go.
        navigate(returnToPath);
    };

    //trigger refresh after 5, 15, 25 ratings, etc.
    useEffect(() => {
        // Guard Clause: If a refresh has already been done for this rating count, do nothing.
        if (totalSessionRatings > 0 && totalSessionRatings === lastRefreshCount.current) return;

        const handleRefresh = () => {
            refreshTrendsQueue(trendsCurrentIndex, trendsQueue);
            // After triggering the refresh, "remember" this rating count.
            lastRefreshCount.current = totalSessionRatings;
        };

        if (!hasDoneFirstRefresh.current && totalSessionRatings >= 5) {
            handleRefresh();
            hasDoneFirstRefresh.current = true;
        } else if (
            hasDoneFirstRefresh.current &&
            totalSessionRatings > 5 &&
            (totalSessionRatings - 5) % 10 === 0
        ) {
            handleRefresh();
        }
    }, [totalSessionRatings, trendsCurrentIndex, trendsQueue, refreshTrendsQueue]);

    //trigger refresh when nearing end of trendsQueue
    useEffect(() => {
        if (playSource !== 'Trends') return;
        const threshold = 5;
        const isNearEnd = trendsQueue.length > 0 && trendsCurrentIndex >= trendsQueue.length - threshold;

        if (isNearEnd) {
            refreshTrendsQueue(trendsCurrentIndex, trendsQueue);
        }
    }, [playSource, trendsCurrentIndex, trendsQueue, refreshTrendsQueue]);

    // This is now the single source of truth for logging song streams.
    useEffect(() => {
        if (!navigator.onLine) return;
        
        const audio = audioRef.current;
        const activeSong = currentSong;
        if (!activeSong) return;

        // --- Part 1: Initialize the Log ---
        // Open the lock for the new track and prime the log if empty.
        if (playbackEvents.current.length === 0) {
            lastLoggedId.current = null; // Clear lock for the new track
            const startTime = audio.currentTime;
            
            playbackEvents.current = [{ 
                type: 'play', 
                time: startTime, 
                timestamp: Date.now() 
            }];
        } else {
            // If it was already primed (e.g. by performRadioSync), just release the lock.
            lastLoggedId.current = null;
        }

        // --- Part 2: Handle Page Unload ---
        const handleUnload = () => {
            if (playbackEvents.current.length > 0 && lastLoggedId.current !== activeSong.id) {
                const finalEvents = [
                    ...playbackEvents.current,
                    { type: 'pause', time: audio.currentTime, timestamp: Date.now() }
                ];
                logStream({
                    songId: activeSong.id,
                    artistId: activeSong.artistId,
                    source: playSource,
                    events: finalEvents,
                }).catch(err => console.error("Failed to log stream on unload:", err));
            }
        };

        window.addEventListener('beforeunload', handleUnload);

        // --- Part 3: The Cleanup Function (Safety Net for Skips and Source changes) ---
        return () => {
            window.removeEventListener('beforeunload', handleUnload);
            if (!navigator.onLine) return;
            // ✅ SAFEGUARD: Use lastKnownTimeRef if audio.currentTime has already been swept to 0
            const rawCurrentTime = audio.currentTime;
            const closingTime = (rawCurrentTime === 0 && lastKnownTimeRef.current > 1) 
                ? lastKnownTimeRef.current 
                : rawCurrentTime;

            const closingEvents = [...playbackEvents.current];
            const closingSongId = activeSong.id;
            const closingArtistId = activeSong.artistId;
            const closingType = activeSong.type;

            // Only fire if this specific song hasn't been logged by handleSongEnd
            if (
                closingEvents.length > 0 && 
                lastLoggedId.current !== closingSongId && 
                closingArtistId && 
                closingType !== 'program_segment'
            ) {
                
                const finalEvents = [
                    ...closingEvents,
                    { type: 'pause', time: closingTime, timestamp: Date.now() }
                ];

                logStream({
                    songId: closingSongId,
                    artistId: closingArtistId,
                    source: playSource,
                    events: finalEvents,
                }).catch(err => console.error("Cleanup log failed", err));
            }
            
            // Clean slate for the incoming song
            playbackEvents.current = [];
        };

    }, [currentSong?.id, playSource, logStream]);

    // 1. Separate Keep-Alive Logic
    useEffect(() => {
        if (!('mediaSession' in navigator) || !currentSong) return;

        // 1. Metadata setup
        navigator.mediaSession.metadata = new MediaMetadata({
            title: currentSong.title || "Unknown Title",
            artist: currentSong.artistName || "Atomic Artist",
            album: currentSong.album || "Atomic Music",
            artwork: [
                { 
                    src: currentSong.resolvedArtworkUrl || currentSong.albumArtworkURL || "/logo512.png", 
                    sizes: '512x512', 
                    type: 'image/png' 
                }
            ]
        });

        // 2. Keep playbackState updated on OS lock screen
        navigator.mediaSession.playbackState = isPlaying ? "playing" : "paused";

        // 3. Action handlers
        const actionHandlers = [
            ['play', () => togglePlayPause()],
            ['pause', () => togglePlayPause()],
            ['nexttrack', () => playNext()],
            ['previoustrack', () => playPrevious()],
            ['seekto', (details) => {
                if (details.seekTime && audioRef.current) {
                    audioRef.current.currentTime = details.seekTime;
                }
            }]
        ];

        actionHandlers.forEach(([action, handler]) => {
            try {
                navigator.mediaSession.setActionHandler(action, handler);
            } catch (error) {
                console.warn(`MediaSession action "${action}" not supported.`);
            }
        });

    }, [currentSong, isPlaying, togglePlayPause, playNext, playPrevious]);

    useEffect(() => {
        if (masterQueue.length === 0 || !currentSong) return;

        const preloadSongs = () => {
            // Define the size of our loading window (e.g., 2 ahead and 2 behind)
            const preloadWindow = 5;

            // Pre-load the next songs
            for (let i = 1; i <= preloadWindow; i++) {
                const nextIndex = (currentIndex + i) % masterQueue.length;
                getHydratedSong(masterQueue[nextIndex]?.id); // This warms up the cache
            }

            // Pre-load the previous songs
            for (let i = 1; i <= preloadWindow; i++) {
                const prevIndex = (currentIndex - i + masterQueue.length) % masterQueue.length;
                getHydratedSong(masterQueue[prevIndex]?.id);
            }
        };

        // Update the immediately visible next/previous songs for the UI
        const nextIndex = (currentIndex + 1) % masterQueue.length;
        getHydratedSong(masterQueue[nextIndex]?.id).then(setNextSong);

        const prevIndex = (currentIndex - 1 + masterQueue.length) % masterQueue.length;
        getHydratedSong(masterQueue[prevIndex]?.id).then(setPreviousSong);

        // Run the main pre-loading in the background
        preloadSongs();

    }, [currentIndex, masterQueue, getHydratedSong, currentSong]);

    const handleSongEnd = useCallback(async () => {
        const audio = audioRef.current;
        if (!audio) return;

        // 1. Capture the final position and add the completion event
        const finalTime = audio.duration || audio.currentTime;
        playbackEvents.current.push({ 
            type: 'end', 
            time: finalTime, 
            timestamp: Date.now() 
        });

        // 2. Dispatch stream metrics to backend immediately
        if (currentSong && playbackEvents.current.length > 0) {
            if (currentSong.artistId && currentSong.type !== 'program_segment') {
                // Lock this ID so the useEffect cleanup function doesn't send duplicate logs
                lastLoggedId.current = currentSong.id;
                
                const eventsToLog = [...playbackEvents.current];
                
                logStream({
                    songId: currentSong.id,
                    artistId: currentSong.artistId,
                    source: playSource,
                    events: eventsToLog,
                }).catch((err) => console.error("Stream log failed on track end:", err));
            }
        }

        // 3. Clear the log array for the next incoming track
        playbackEvents.current = [];

        // 4. Radio mode check
        if (playSource === 'Radio' && radioDataRef.current) {
            performRadioSync(radioDataRef.current);
            return;
        }

        // 5. Normal queue transition
        if (repeatMode === 'song') {
            audio.currentTime = 0;
            playbackEvents.current = [{ type: 'play', time: 0, timestamp: Date.now() }];
            audio.play().catch((err) => console.warn("Song repeat play blocked:", err));
            setIsPlaying(true);
        } else if (repeatMode === 'off' && currentIndex >= masterQueue.length - 1 && !isShuffleOn) {
            // True end of queue: Stop audio session gracefully
            setIsPlaying(false);
            audio.pause();
            if (playSource !== 'Trends' && trendsQueue.length > 0) {
                setQueueAndPlay(trendsQueue, 0, 'Trends');
            }
        } else {
            // Proceed directly to next song without pausing the audio session
            playNext();
        }
    }, [
        currentSong, 
        playSource, 
        repeatMode, 
        currentIndex, 
        masterQueue, 
        isShuffleOn, 
        trendsQueue, 
        performRadioSync, 
        playNext, 
        setQueueAndPlay, 
        logStream
    ]);

    // EFFECT TO MANAGE AUDIO EVENTS (ended, seeked, and background wake-up)
    useEffect(() => {
        const audio = audioRef.current;
        if (!audio) return;

        // 1. THE PROACTIVE WAKE-UP (The "Stay Hot" PING)
        const handleTimeUpdate = () => {
            // Keep track of the active playhead *before* any seek actions happen
            lastKnownTimeRef.current = audio.currentTime;

            const remaining = audio.duration - audio.currentTime;
            if (remaining > 0 && remaining < 2) {
                if ('mediaSession' in navigator) {
                    navigator.mediaSession.playbackState = "playing";
                }
            }
        };

        // Capture where the user *started* their scrub from
        const handleSeeking = () => {
            if (playbackEvents.current.length > 0) {
                const originTime = lastKnownTimeRef.current;

                
                playbackEvents.current.push({ 
                    type: 'seek', 
                    time: originTime, 
                    timestamp: Date.now() 
                });
            }
        };

        // Capture where the user *landed*
        const handleSeeked = () => {
            if (isPlaying) {
                const destinationTime = audio.currentTime;

                
                playbackEvents.current.push({ 
                    type: 'play', 
                    time: destinationTime, 
                    timestamp: Date.now() 
                });
            }
        };

        // Attach Listeners
        audio.addEventListener('timeupdate', handleTimeUpdate);
        audio.addEventListener('seeking', handleSeeking);
        audio.addEventListener('seeked', handleSeeked);
        audio.addEventListener('ended', handleSongEnd);

        // Cleanup function
        return () => {
            audio.removeEventListener('timeupdate', handleTimeUpdate);
            audio.removeEventListener('seeking', handleSeeking);
            audio.removeEventListener('seeked', handleSeeked);
            audio.removeEventListener('ended', handleSongEnd);
        };
    }, [
        playNext, 
        repeatMode, 
        currentSong, 
        currentIndex, 
        masterQueue, 
        isShuffleOn, 
        playSong, 
        playSource, 
        trendsQueue, 
        setQueueAndPlay, 
        performRadioSync,
        handleSongEnd
    ]);

    // Cleanup Radio on Unmount
    useEffect(() => {
        return () => {
            if (radioUnsubscribeRef.current) radioUnsubscribeRef.current();
        };
    }, []);

    // ✅ ADD THIS NEW useEffect TO HANDLE DIRECT LINKS
    useEffect(() => {
        const path = location.pathname;
        const isOnSongPage = path.startsWith('/song/');
        
        // If the player is already initialized and playing, NEVER run this setup block
        if (isInitializedRef.current) return;

        if (isOnSongPage && !currentSong) {
            const songIdFromUrl = path.split('/').pop();

            if (songIdFromUrl) {
                isInitializedRef.current = true; // Set lock immediately
                
                (async () => {
                    const songRef = doc(db, "songs", songIdFromUrl);
                    try {
                        const docSnap = await getDoc(songRef);
                        if (docSnap.exists()) {
                            const songData = { id: docSnap.id, ...docSnap.data() };
                            setQueueAndLoad([songData], 0, 'Direct Link');
                        }
                    } catch (error) {
                        console.error("Error fetching direct link song:", error);
                        isInitializedRef.current = false; // Release lock on failure
                    }
                })();
            }
        } else if (currentSong) {
            // Once any song is loaded or played natively, lock the direct link initializer
            isInitializedRef.current = true;
        }
    }, [location.pathname, currentSong, db, setQueueAndLoad]);

    // --- OTHER PLAYER CONTROLS (playNext, pause, etc.) ---



    // --- FINAL CONTEXT VALUE ---
    // Combine all values needed by consumer components
    const value = useMemo(() => ({
        // Core Player State & Controls
        currentSong,
        nextSong,
        previousSong,
        isPlaying,
        playSource,
        setPlaySource,
        playSong,
        loadSong,
        tuneInToRadio,
        getRadioSyncData,
        isExpanded,
        setReturnToPath,
        closePlayer, 
        audioRef,
        isShuffleOn,
        repeatMode,
        setIsExpanded,
        setQueueAndPlay,
        setQueueAndLoad,
        playNext,
        playPrevious,
        togglePlayPause,
        toggleShuffle,
        toggleRepeatMode,
        replaceUpcomingQueue,
        isMuted, 
        toggleMute,
        setMuteExplicit,
        leaveRadio,

        // NEW Trends State for UI
        trendsQueue,
        trendsCurrentIndex,
        hydratedSongs,
        trendsIsLoading,

        // Trends Controls
        startTrendsPlayback,
        incrementRatingCount,
        hasDismissedTrendsIntro,
        dismissTrendsIntro,
        isMicroPlayer,
        setIsMicroPlayer
    }), [
        // ✅ FIX: Add ALL values and functions from the object above to this dependency array.
        currentSong,
        nextSong,
        previousSong,
        isPlaying,
        playSource,
        playSong,
        loadSong,
        isExpanded,
        audioRef,
        isShuffleOn,
        repeatMode,
        setIsExpanded,
        setReturnToPath, 
        closePlayer,
        setQueueAndPlay,
        setQueueAndLoad,
        playNext,
        playPrevious,
        togglePlayPause,
        toggleShuffle,
        toggleRepeatMode,
        replaceUpcomingQueue,
        trendsQueue,
        trendsCurrentIndex,
        hydratedSongs,
        trendsIsLoading,
        startTrendsPlayback,
        incrementRatingCount,
        tuneInToRadio,
        getRadioSyncData,
        isMuted, 
        toggleMute,
        setMuteExplicit,
        leaveRadio,
        hasDismissedTrendsIntro,
        dismissTrendsIntro,
        isMicroPlayer,
        setIsMicroPlayer
    ]);

    return (
        <MusicContext.Provider value={value}>
            {children}
        </MusicContext.Provider>
    );
};